import React from 'react';
import {
  CheckCircle,
  Loader2,
  Trash2,
  Sparkles,
  AlertTriangle,
  Shield,
  Database,
  Upload,
  CheckCheck,
  Download,
} from 'lucide-react';
import { theme } from '../../styles/theme';
import { DIMENSION_PHASE_ORDER, type DimensionPhase } from './phases';

interface PhaseIndicatorProps {
  currentPhase: string;
  phaseName: string;
  phaseProgress?: number;
}

interface Phase {
  id: string;
  name: string;
  icon: React.ElementType;
  color: string;
}

// Decision: metadata (name/icon/color) is keyed here, but the render ORDER is
// always derived from the shared DIMENSION_PHASE_ORDER (phases.ts) so this
// component and DeduplicationStatsPanel can never silently diverge on phase
// ordering. Record<DimensionPhase, ...> makes the compiler enforce that every
// phase in the shared order has metadata here.
const PHASE_META: Record<DimensionPhase, { name: string; icon: React.ElementType; color: string }> = {
  init: { name: 'Starting', icon: Upload, color: 'blue' },
  loading: { name: 'Loading parts', icon: Download, color: 'blue' },
  pre_dedup: { name: 'Cleaning existing data', icon: Trash2, color: 'purple' },
  ai_validate: { name: 'Checking dimensions', icon: Sparkles, color: 'orange' },
  ai_create: { name: 'Finding new dimensions', icon: Sparkles, color: 'orange' },
  post_dedup: { name: 'Resolving conflicts', icon: AlertTriangle, color: 'yellow' },
  compliance: { name: 'Shape checks', icon: Shield, color: 'green' },
  final_dedup: { name: 'Skipping catalogue duplicates', icon: Database, color: 'cyan' },
  sync: { name: 'Saving', icon: Upload, color: 'blue' },
  complete: { name: 'Done', icon: CheckCheck, color: 'green' },
};

const PHASES: Phase[] = DIMENSION_PHASE_ORDER.map((id) => ({ id, ...PHASE_META[id] }));

export const PhaseIndicator: React.FC<PhaseIndicatorProps> = ({
  currentPhase,
  phaseName,
  phaseProgress = 0,
}) => {
  const currentPhaseIndex = PHASES.findIndex(p => p.id === currentPhase);
  
  const getPhaseStatus = (phaseIndex: number): 'completed' | 'active' | 'pending' => {
    if (phaseIndex < currentPhaseIndex) return 'completed';
    if (phaseIndex === currentPhaseIndex) {
      // 'complete' is a terminal phase — show as completed, not active
      if (PHASES[phaseIndex].id === 'complete') return 'completed';
      return 'active';
    }
    return 'pending';
  };
  
  const getStatusIcon = (phase: Phase, phaseIndex: number) => {
    const status = getPhaseStatus(phaseIndex);
    const Icon = phase.icon;
    
    if (status === 'completed') {
      return <CheckCircle className="w-5 h-5 text-qc-success" />;
    } else if (status === 'active') {
      return <Loader2 className="w-5 h-5 text-text-secondary animate-spin" />;
    } else {
      return <Icon className="w-5 h-5 text-text-tertiary" />;
    }
  };
  
  return (
    <div className="bg-bg-surface border border-border rounded-xl p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className={`text-sm font-semibold ${theme.text.primary}`}>
          Processing Phases
        </h3>
        <span className={`text-xs ${theme.text.tertiary}`}>
          Current: {phaseName}
        </span>
      </div>
      
      {/* Horizontal stepper (all widths). A grid of equal (1fr) columns keeps
          the circles evenly spaced and on one baseline; the connector is drawn
          behind the circles at their vertical centre (top-5 for an h-10 circle),
          spanning one column to the next, and the circle's solid bg (z-10) masks
          it. Each label sits in a fixed-height, centred box so every column is
          the same height regardless of how many lines the label wraps to — this
          is what makes the row read as a clean, aligned grid. */}
      <div
        className="grid gap-1"
        style={{ gridTemplateColumns: `repeat(${PHASES.length}, minmax(0, 1fr))` }}
      >
        {PHASES.map((phase, index) => {
          const status = getPhaseStatus(index);
          const isLast = index === PHASES.length - 1;

          return (
            <div key={phase.id} className="relative flex flex-col items-center px-0.5">
              {/* Connector to the next step (behind the circles) */}
              {!isLast && (
                <div className="absolute top-5 left-1/2 h-0.5 w-full bg-border-default" />
              )}
              <div
                className={` relative z-10 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${status === 'completed' ? 'bg-qc-success/10' : ''} ${status === 'active' ? 'bg-bg-subtle ring-2 ring-border-strong' : ''} ${status === 'pending' ? 'bg-bg-subtle' : ''} `}
              >
                {getStatusIcon(phase, index)}
              </div>
              <span
                className={` block mt-1.5 h-10 overflow-hidden text-[11px] leading-tight text-center font-medium ${status === 'active' ? theme.text.primary : theme.text.tertiary} `}
              >
                {phase.name}
              </span>
            </div>
          );
        })}
      </div>
      
      {/* Phase Progress Bar (for active phase) */}
      {currentPhaseIndex >= 0 && phaseProgress > 0 && (
        <div className="mt-4">
          <div className="flex justify-between text-xs text-text-tertiary mb-1">
            <span>Phase Progress</span>
            <span>{phaseProgress}%</span>
          </div>
          <div className="w-full bg-bg-hover rounded-full h-1.5">
            <div
              className="bg-interactive-primary h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${phaseProgress}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
};
